/**
 * Le bloc « Gamme / Palier / Ajouter les pièces du palier » en tête des articles d'un bon (2026-10-18).
 *
 * Demande de Benjamin : « quand je crée un bon, je veux choisir une gamme, rajouter ou enlever une pièce ». La gamme de la
 * machine est déjà choisie (c'est celle de la fiche) ; une machine qui n'en a pas (un CSD102) peut prendre n'importe quelle
 * gamme en vigueur du MÊME constructeur. Le palier se choisit ici ; le bouton verse ses pièces dans la liste des articles,
 * où chaque ligne reste modifiable et retirable.
 *
 * Quand la gamme est celle de la machine ET que la nature enregistre un entretien, le palier d'ici est le MÊME que « Palier de la
 * gamme réalisé » : si l'un change, l'autre suit. Ailleurs (une autre gamme, ou une nature qui n'enregistre pas d'entretien) il ne sert
 * qu'à choisir les pièces : il ne déclare aucun entretien réalisé.
 *
 * Verser un autre palier REMPLACE ce que le bouton avait versé (les paliers sont cumulatifs) : c'est l'éditeur qui le fait, après
 * confirmation ; les lignes saisies à la main restent.
 *
 * Le composant ne verse rien lui-même : il rend le palier choisi (`onVerser`), l'éditeur écrit dans le bon et contrôle les
 * références au tarif. Aucune gamme n'est jamais modifiée d'ici.
 */
import { useEffect, useMemo, useState } from 'react';

import { ApiError, api, type GammeNiveau, type GammeContenu, type GammeResume, type MaintenanceMachine } from '../api/client';
import { contenuEnVigueur, libelleDuBouton, paliersDeGamme } from '../domaine/bon-gamme';

export interface GammeChoisie {
  readonly id: string;
  readonly label: string;
}

export function BlocGammeBon({
  maintenance,
  constructeurId,
  levelKeyMachine,
  palierSynchronise,
  onChoisirPalierRealise,
  onVerser,
  desactive,
  enCours,
}: {
  /** La gamme rattachée à la machine, ou `null` (non lue, ou aucune). */
  maintenance: MaintenanceMachine | null;
  /** Le constructeur de la machine (marque, à défaut fabricant) : ses autres gammes se proposent. `null` : aucune autre. */
  constructeurId: string | null;
  /** Le palier réalisé du bon (la liste « Palier de la gamme réalisé »). */
  levelKeyMachine: string | null;
  /** La nature enregistre-t-elle un entretien (le palier d'ici est alors le palier réalisé du bon) ? */
  palierSynchronise: boolean;
  onChoisirPalierRealise: (levelKey: string | null) => void;
  onVerser: (niveau: GammeNiveau, gamme: GammeChoisie) => void;
  /** Le bon est en cours d'enregistrement. */
  desactive: boolean;
  /** Les références du dernier versement sont en cours de contrôle au tarif. */
  enCours: boolean;
}) {
  const rattachement = maintenance?.rattachement ?? null;
  const contenuMachine: GammeContenu | null = rattachement?.version?.contenu ?? null;

  const [gammes, setGammes] = useState<readonly GammeResume[] | null>(null);
  const [choix, setChoix] = useState<string | null>(null);
  const [palierLocal, setPalierLocal] = useState('');
  const [detail, setDetail] = useState<{ readonly id: string; readonly contenu: GammeContenu | null } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  // Les autres gammes du constructeur : en vigueur, non archivées. La liste est lue une fois (73 gammes au plus).
  useEffect(() => {
    if (!constructeurId) {
      setGammes([]);
      return;
    }
    let annule = false;
    void api
      .maintenanceProfiles()
      .then((liste) => {
        if (!annule) setGammes(liste.filter((g) => g.brandPartyId === constructeurId && g.archivedAt === null && g.enVigueur !== null));
      })
      .catch(() => {
        if (!annule) {
          setGammes([]);
          setErreur('La liste des gammes n’a pas pu être lue : seule la gamme de la machine est proposée.');
        }
      });
    return () => {
      annule = true;
    };
  }, [constructeurId]);

  const idMachine = rattachement?.profile.id ?? null;
  const autres = useMemo(
    () => [...(gammes ?? [])].filter((g) => g.id !== idMachine).sort((a, b) => a.label.localeCompare(b.label, 'fr')),
    [gammes, idMachine],
  );

  // La gamme choisie : celle de la machine tant qu'on n'a rien choisi d'autre (et qu'elle a une version en vigueur).
  const effectif = choix ?? (contenuMachine ? 'machine' : '');
  const estCelleDeLaMachine = effectif === 'machine';

  // Le détail d'une autre gamme (sa version en vigueur) : lu au choix.
  useEffect(() => {
    if (effectif === '' || effectif === 'machine') return;
    if (detail?.id === effectif) return;
    let annule = false;
    setErreur(null);
    void (async () => {
      try {
        const d = await api.maintenanceProfile(effectif);
        if (annule) return;
        setDetail({ id: effectif, contenu: contenuEnVigueur(d) });
      } catch (e) {
        if (annule) return;
        setDetail({ id: effectif, contenu: null });
        setErreur(e instanceof ApiError && e.status === 403 ? 'Vous n’avez pas le droit de lire les gammes.' : 'Cette gamme n’a pas pu être lue.');
      }
    })();
    return () => {
      annule = true;
    };
  }, [effectif, detail?.id]);

  // Une autre gamme dont la version en vigueur n'est pas encore lue : le bouton attend (rien ne se verse d'un contenu inconnu).
  const chargementDetail = effectif !== '' && !estCelleDeLaMachine && detail?.id !== effectif;
  const contenu = estCelleDeLaMachine ? contenuMachine : effectif !== '' && detail?.id === effectif ? detail.contenu : null;
  const paliers = paliersDeGamme(contenu);
  const synchro = estCelleDeLaMachine && palierSynchronise;
  const levelKeyChoisi = synchro ? (levelKeyMachine ?? '') : palierLocal;
  const palier = paliers.find((p) => p.levelKey === levelKeyChoisi) ?? null;

  const nomDeLaGamme = estCelleDeLaMachine ? (rattachement?.profile.label ?? '') : (autres.find((g) => g.id === effectif)?.label ?? '');

  // Rien à proposer : ni gamme sur la machine, ni autre gamme du constructeur.
  if (!contenuMachine && autres.length === 0) return null;

  const choisirPalier = (levelKey: string) => {
    if (synchro) onChoisirPalierRealise(levelKey === '' ? null : levelKey);
    else setPalierLocal(levelKey);
  };

  const verser = () => {
    const niveau = contenu?.levels.find((n) => n.levelKey === levelKeyChoisi);
    const id = estCelleDeLaMachine ? idMachine : effectif;
    if (!niveau || !id) return;
    onVerser(niveau, { id, label: nomDeLaGamme });
  };

  return (
    <div className="bloc-gamme-bon">
      <div className="rangee">
        <label htmlFor="bon-gamme">Gamme</label>
        <select
          id="bon-gamme"
          value={effectif}
          disabled={desactive}
          onChange={(e) => {
            setChoix(e.target.value);
            setPalierLocal('');
            setErreur(null);
          }}
        >
          {contenuMachine && rattachement ? (
            <option value="machine">{rattachement.profile.label} — celle de cette machine</option>
          ) : (
            <option value="">— Choisir une gamme —</option>
          )}
          {autres.map((g) => (
            <option key={g.id} value={g.id}>
              {g.label}
            </option>
          ))}
        </select>
        <span className="aide">
          {contenuMachine
            ? 'La gamme de la machine est déjà choisie. Vous pouvez en prendre une autre du même constructeur.'
            : 'Cette machine n’a pas de gamme rattachée : prenez une gamme du même constructeur.'}
        </span>
      </div>

      <div className="rangee">
        <label htmlFor="bon-gamme-palier">Palier</label>
        <select
          id="bon-gamme-palier"
          value={levelKeyChoisi}
          disabled={desactive || paliers.length === 0}
          onChange={(e) => choisirPalier(e.target.value)}
        >
          <option value="">— Choisir —</option>
          {paliers.map((p) => (
            <option key={p.levelKey} value={p.levelKey}>
              {p.libelle}
            </option>
          ))}
        </select>
        <span className="aide">
          {effectif === ''
            ? 'Choisissez d’abord une gamme.'
            : synchro
              ? 'Le même que « Palier de la gamme réalisé » : si vous changez l’un, l’autre suit.'
              : estCelleDeLaMachine
                ? 'Ce modèle de bon n’enregistre pas d’entretien réalisé : ce palier ne sert qu’à choisir les pièces.'
                : 'Cette gamme n’est pas celle de la machine : ce palier ne sert qu’à choisir les pièces, il ne déclare aucun entretien.'}
        </span>
      </div>

      <div className="rangee-bouton">
        <button type="button" disabled={desactive || enCours || chargementDetail || !palier || palier.pieces === 0} onClick={verser}>
          {chargementDetail ? 'Lecture de la gamme…' : libelleDuBouton(palier)}
        </button>
        <span className="aide">
          Elles remplacent celles que ce bouton avait déjà ajoutées (les paliers sont cumulatifs : un autre palier ne s’ajoute pas
          par-dessus) ; les lignes saisies à la main ne bougent pas. Sans prix : retirez ce qui n’a pas été consommé, ajoutez ce qui
          manque. La gamme elle-même ne change pas, seul ce bon est touché.
        </span>
      </div>

      {erreur && <p className="aide erreur-champ">{erreur}</p>}
    </div>
  );
}
